import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { NetworkStatus, useLazyQuery } from '@apollo/client'
import { ActionIcon, Menu, Tooltip } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'

import { useQuery } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import DNButton from 'src/components/Buttons/Button/DNButton'
import BulkActionBar from 'src/components/Core/BulkActionBar/BulkActionBar'
import DnTable from 'src/components/Core/DnTable'
import FilterMenu from 'src/components/Overlays/FiltersMenu/FilterMenu'
import { toast } from 'src/components/Overlays/Toast/Toast'
import CallerIdShield, {
  callerIdStatusLabel,
} from 'src/components/VerifiedCallerId/CallerIdShield'
import { GET_EMPLOYEES } from 'src/graphql/employees.graphql'
import { GET_ALL_PERMISSIONS } from 'src/graphql/permissions.graphql'
import {
  VerifiedCallerIdOwnerType,
  type VerifiedCallerIdStatus,
} from 'src/graphql/types/verifiedCallerIds'
import { GET_VERIFIED_CALLER_IDS } from 'src/graphql/verifiedCallerIds.graphql'
import IconCheckmark from 'src/icons/IconCheckmark'
import IconClose from 'src/icons/IconClose'
import IconCopy from 'src/icons/IconCopy'
import IconThreeDots from 'src/icons/IconThreeDots'
import { hasPermission, isAdmin, isAdminOrOps } from 'src/lib/auth/permissions'
import { buildFullName } from 'src/lib/formatters'
import { PERMISSION_KEYS } from 'src/lib/permissions'
import { formatPhone, usDigits } from 'src/lib/phone.utils'

import AddUserModal from './components/AddUserModal'
import BulkPermissionsModal from './components/BulkPermissionsModal'
import RemoveUserModal from './components/RemoveUserModal'
import UserDetailsDrawer from './components/UserDetailsDrawer'

const LIMIT = 20

// The list is scoped to recruiters so the roster stays readable when shared on
// screen. There is deliberately no Role section in the filter menu; the scope
// only widens on its own when a user of another role is added (see
// handleUserAdded), and any permission filter narrows within whatever scope is
// currently active.
const DEFAULT_ROLE_SCOPE = ['RECRUITER']

/**
 * Table cells clip rather than spill: the table is fixed-layout, so a long
 * value (usually an email) would otherwise run into the next column. The full
 * value stays available on hover.
 */
const TextCell = ({ value }: { value?: string | null }) => (
  <div
    className="truncate text-xs text-doubleNickel-gray-500"
    title={value || undefined}
  >
    {value}
  </div>
)

const UsersTab = () => {
  const { currentUser } = useAuth()
  const isAdminOrOpsUser = isAdminOrOps(currentUser)
  const isAdminUser = isAdmin(currentUser)
  const canRemoveUser =
    isAdminOrOpsUser ||
    hasPermission(currentUser, PERMISSION_KEYS.MANAGE_REMOVE_USER)
  const canManageUserAccess =
    isAdminUser ||
    hasPermission(currentUser, PERMISSION_KEYS.MANAGE_USER_ACCESS)
  const [addModalOpened, addModalHandlers] = useDisclosure(false)
  const [removeModalOpened, removeModalHandlers] = useDisclosure(false)
  const [detailsDrawerOpened, detailsDrawerHandlers] = useDisclosure(false)
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(
    null
  )

  // Handle Nylas OAuth callback
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search)
    const nylasCode = queryParams.get('nylasCode')
    const storedEmployeeId = sessionStorage.getItem('nylas-employee-id')

    if (nylasCode && storedEmployeeId) {
      // Auto-open the drawer for the employee who initiated OAuth
      setSelectedEmployeeId(storedEmployeeId)
      detailsDrawerHandlers.open()
      // Clean up sessionStorage
      sessionStorage.removeItem('nylas-employee-id')
    }
  }, [detailsDrawerHandlers])
  const [employeePendingRemoval, setEmployeePendingRemoval] = useState(null)
  const [currentPageInfo, setCurrentPageInfo] = useState({
    limit: LIMIT,
    offset: 0,
  })
  const currentPageInfoRef = useRef(currentPageInfo)

  const [roleFilter, setRoleFilter] = useState<string[]>(DEFAULT_ROLE_SCOPE)
  const [permissionFilter, setPermissionFilter] = useState<string[]>([])
  const [paginationResetKey, setPaginationResetKey] = useState(0)

  // Permission options come from the catalog, loaded when the filter section is
  // first opened rather than on every visit to the tab.
  const [permissionOptions, setPermissionOptions] = useState<
    { label: string; value: string }[]
  >([])
  const [permissionOptionsLoaded, setPermissionOptionsLoaded] = useState(false)
  const [loadPermissionOptions] = useLazyQuery(GET_ALL_PERMISSIONS, {
    onCompleted: (data) => {
      setPermissionOptions(
        (data?.permissions ?? []).map((permission) => ({
          label: permission.description || permission.key,
          value: permission.key,
        }))
      )
      setPermissionOptionsLoaded(true)
    },
    onError: () => {
      toast('Failed to load permissions', 'error')
      // Mark the load as settled even though it failed: FilterMenu shows
      // "Loading…" while loadData is present, isLoaded is false and there are
      // no options, so leaving this false strands the section on a spinner.
      // Reopening the section still retries, since FilterMenu calls loadData
      // on every open rather than gating on isLoaded.
      setPermissionOptionsLoaded(true)
    },
  })

  const employeeFilters = useMemo(
    () => ({
      roleTypes: roleFilter.length > 0 ? roleFilter : DEFAULT_ROLE_SCOPE,
      ...(permissionFilter.length > 0
        ? { permissionKeys: permissionFilter }
        : {}),
    }),
    [roleFilter, permissionFilter]
  )

  // Any filter change invalidates the current page, so send the query back to
  // page 1 and remount the table to reset its internal pagination state.
  const resetPagination = useCallback(() => {
    setCurrentPageInfo({ limit: LIMIT, offset: 0 })
    setPaginationResetKey((key) => key + 1)
  }, [])

  const handlePermissionFilterChange = useCallback(
    (selected: string[]) => {
      setPermissionFilter(selected)
      resetPagination()
    },
    [resetPagination]
  )

  // A newly added user whose role isn't in the active scope would be created
  // successfully but never appear in the list, which reads as a failed add.
  const handleUserAdded = useCallback(
    (roleType: string) => {
      if (!roleType || roleFilter.includes(roleType)) return
      setRoleFilter([...roleFilter, roleType])
      resetPagination()
    },
    [roleFilter, resetPagination]
  )

  const {
    data,
    loading,
    refetch: refetchEmployees,
    networkStatus,
  } = useQuery(GET_EMPLOYEES, {
    notifyOnNetworkStatusChange: true,
    variables: {
      pageInfo: currentPageInfo,
      filters: employeeFilters,
    },
  })

  const employees = useMemo(() => data?.employees?.items ?? [], [data])

  // One fetch for the whole list: which employee phones are verified caller IDs.
  const { data: callerIdData } = useQuery(GET_VERIFIED_CALLER_IDS, {
    fetchPolicy: 'network-only',
  })
  const callerIdRowsByEmployee = useMemo(() => {
    const byEmployee = new Map<
      string,
      { digits: string; status: VerifiedCallerIdStatus }[]
    >()
    for (const row of callerIdData?.verifiedCallerIds ?? []) {
      if (
        row.ownerType === VerifiedCallerIdOwnerType.EMPLOYEE &&
        row.employeeId
      ) {
        const rows = byEmployee.get(row.employeeId) ?? []
        rows.push({ digits: usDigits(row.phoneNumber), status: row.status })
        byEmployee.set(row.employeeId, rows)
      }
    }
    return byEmployee
  }, [callerIdData])
  const callerIdStatusFor = useCallback(
    (employee: { employeeId: string; phone?: string | null }) => {
      // Only a row for the phone currently on the profile counts.
      const digits = usDigits(employee.phone)
      return (
        callerIdRowsByEmployee
          .get(employee.employeeId)
          ?.find((row) => row.digits === digits)?.status ?? null
      )
    },
    [callerIdRowsByEmployee]
  )
  const totalCount = data?.employees?.totalCount ?? 0

  currentPageInfoRef.current = currentPageInfo

  // Bulk permission selection (admin or MANAGE_USER_ACCESS grant only)
  const [bulkSelectedIds, setBulkSelectedIds] = useState<Set<string>>(new Set())
  const [bulkPermissionMode, setBulkPermissionMode] = useState<
    'grant' | 'revoke' | null
  >(null)

  // Clear selection when the visible set of rows changes. Keyed on the page and
  // filter rather than on `data`, so an unrelated drawer action that refetches
  // the list (saving a user, connecting a calendar) doesn't silently drop it.
  useEffect(() => {
    setBulkSelectedIds(new Set())
  }, [currentPageInfo, employeeFilters])

  const allVisibleIds = useMemo(
    () => employees.map((employee) => employee.employeeId),
    [employees]
  )
  const allVisibleSelected =
    allVisibleIds.length > 0 &&
    allVisibleIds.every((employeeId) => bulkSelectedIds.has(employeeId))

  const toggleBulkSelect = useCallback((employeeId: string) => {
    setBulkSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(employeeId)) {
        next.delete(employeeId)
      } else {
        next.add(employeeId)
      }
      return next
    })
  }, [])

  const toggleSelectAll = useCallback(() => {
    setBulkSelectedIds(allVisibleSelected ? new Set() : new Set(allVisibleIds))
  }, [allVisibleSelected, allVisibleIds])

  // Names travel with the ids so the bulk modal can report failures per user.
  const selectedEmployeeTargets = useMemo(
    () =>
      employees
        .filter((employee) => bulkSelectedIds.has(employee.employeeId))
        .map((employee) => ({
          employeeId: employee.employeeId,
          name:
            buildFullName(employee) || employee.email || employee.employeeId,
        })),
    [employees, bulkSelectedIds]
  )

  const handleRefetchEmployees = useCallback(() => {
    return refetchEmployees({
      pageInfo: currentPageInfoRef.current,
      filters: employeeFilters,
    })
  }, [refetchEmployees, employeeFilters])

  const handleRemove = useCallback(
    (employee) => {
      setEmployeePendingRemoval(employee)
      removeModalHandlers.open()
    },
    [removeModalHandlers]
  )

  const handleLoadPage = useCallback(
    ({ offset, limit }) => {
      setCurrentPageInfo({ offset, limit })
      refetchEmployees({
        pageInfo: {
          offset,
          limit,
        },
        filters: employeeFilters,
      })
    },
    [refetchEmployees, setCurrentPageInfo, employeeFilters]
  )

  const handleRowClick = useCallback(
    (rowIndex: number) => {
      const employee = employees[rowIndex]
      if (employee) {
        setSelectedEmployeeId(employee.employeeId)
        detailsDrawerHandlers.open()
      }
    },
    [employees, detailsDrawerHandlers]
  )

  const columns = useMemo(
    () => [
      ...(canManageUserAccess
        ? [
            {
              key: 'select',
              label: (
                <input
                  type="checkbox"
                  aria-label="Select all users on this page"
                  checked={allVisibleSelected}
                  onChange={toggleSelectAll}
                  onClick={(e) => e.stopPropagation()}
                />
              ),
              width: 3,
              render: (employee) => (
                <input
                  type="checkbox"
                  aria-label={`Select ${buildFullName(employee)}`}
                  checked={bulkSelectedIds.has(employee.employeeId)}
                  onChange={() => toggleBulkSelect(employee.employeeId)}
                  onClick={(e) => e.stopPropagation()}
                />
              ),
            },
          ]
        : []),
      {
        key: 'employeeId',
        label: 'ID',
        width: 4,
        render: (employee) => (
          <Tooltip label="Copy employee ID">
            <ActionIcon
              size={14}
              variant="subtle"
              onClick={(e) => {
                e.stopPropagation()
                toast('Copied employee ID to clipboard', 'success')
                navigator.clipboard.writeText(employee.employeeId)
              }}
            >
              <IconCopy className="fill-none stroke-doubleNickel-gray-500" />
            </ActionIcon>
          </Tooltip>
        ),
      },
      // Name, Email and Job Title carry no width: DnTable splits whatever is
      // left over between them, so the row always totals 100% and stays correct
      // if a column is added or removed later.
      {
        key: 'name',
        label: 'Name',
        render: (employee) => <TextCell value={buildFullName(employee)} />,
      },
      {
        key: 'email',
        label: 'Email',
        render: (employee) => <TextCell value={employee.email} />,
      },
      {
        key: 'phone',
        label: 'Phone',
        // Fixed-format content, so it gets a fixed share
        width: 12,
        render: (employee) => (
          <div className="flex w-full min-w-0 flex-row items-center justify-between gap-2">
            <TextCell value={formatPhone(employee.phone)} />
            {employee.phone && (
              <Tooltip label={callerIdStatusLabel(callerIdStatusFor(employee))}>
                <span className="flex items-center">
                  <CallerIdShield status={callerIdStatusFor(employee)} />
                </span>
              </Tooltip>
            )}
          </div>
        ),
      },
      {
        key: 'jobTitle',
        label: 'Job Title',
        render: (employee) => <TextCell value={employee.jobTitle} />,
      },
      {
        key: 'calendarConnected',
        label: 'Calendar',
        width: 8,
        render: (employee) => (
          <div className="flex justify-start">
            {employee.calendarConnected ? (
              <IconCheckmark className="h-4 w-4 fill-none stroke-doubleNickel-success-500" />
            ) : (
              <IconClose className="h-2 w-2 fill-none stroke-doubleNickel-error-500" />
            )}
          </div>
        ),
      },
      {
        key: 'emailConnected',
        label: 'Email',
        width: 8,
        render: (employee) => (
          <div className="flex justify-start">
            {employee.emailConnected ? (
              <IconCheckmark className="h-4 w-4 fill-none stroke-doubleNickel-success-500" />
            ) : (
              <IconClose className="h-2 w-2 fill-none stroke-doubleNickel-error-500" />
            )}
          </div>
        ),
      },
      {
        key: 'mfaEnabled',
        label: 'MFA',
        width: 8,
        render: (employee) => (
          <div className="flex justify-start">
            {employee.mfaEnabled ? (
              <IconCheckmark className="h-4 w-4 fill-none stroke-doubleNickel-success-500" />
            ) : (
              <IconClose className="h-2 w-2 fill-none stroke-doubleNickel-error-500" />
            )}
          </div>
        ),
      },
      {
        key: 'actions',
        label: '',
        width: 3,
        render: (employee) => (
          <div className="flex items-center justify-end">
            {canRemoveUser && (
              <Menu shadow="md" position="bottom-end" withinPortal>
                <Menu.Target>
                  <ActionIcon
                    variant="subtle"
                    className="h-6 w-6"
                    onClick={(e) => {
                      e.stopPropagation()
                    }}
                  >
                    <IconThreeDots className="h-4 w-4 fill-doubleNickel-white stroke-doubleNickel-gray-600" />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown className="w-36">
                  <Menu.Item
                    className="text-doubleNickel-gray-700"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleRemove(employee)
                    }}
                  >
                    Remove
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )}
          </div>
        ),
      },
    ],
    [
      handleRemove,
      canRemoveUser,
      canManageUserAccess,
      callerIdStatusFor,
      allVisibleSelected,
      bulkSelectedIds,
      toggleBulkSelect,
      toggleSelectAll,
    ]
  )

  const tableLoading = loading || networkStatus === NetworkStatus.refetch

  return (
    <div className="flex h-full flex-col gap-6">
      <div className="flex flex-row justify-between ">
        <div className="flex flex-col">
          <div className="text-lg font-semibold text-doubleNickel-gray-900">
            Users
          </div>
          <div className="text-sm text-doubleNickel-gray-600">
            {/* These spans are necessary to prevent Google Translate issues  */}
            <span>{totalCount}</span>
            <span> users</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <BulkActionBar
            selectedCount={bulkSelectedIds.size}
            actions={[
              {
                label: 'Grant permission',
                onClick: () => setBulkPermissionMode('grant'),
              },
              {
                label: 'Revoke permission',
                onClick: () => setBulkPermissionMode('revoke'),
              },
            ]}
          />
          <FilterMenu
            filters={[
              {
                title: 'Permission',
                selected: permissionFilter,
                onChange: handlePermissionFilterChange,
                options: permissionOptions,
                searchable: true,
                loadData: loadPermissionOptions,
                isLoaded: permissionOptionsLoaded,
              },
            ]}
          />
          <DNButton variant="primary" onClick={() => addModalHandlers.open()}>
            Add User
          </DNButton>
        </div>
      </div>
      <div className="min-h-0 flex-1">
        <DnTable
          key={paginationResetKey}
          columns={columns}
          data={employees}
          enableColumnResize={false}
          loading={tableLoading}
          totalCount={totalCount}
          onLoadPage={handleLoadPage}
          defaultResultsPerPage={LIMIT}
          onRowClick={handleRowClick}
          emptyStateMessage="No users found. Add one to get started."
        />
      </div>

      <AddUserModal
        opened={addModalOpened}
        close={addModalHandlers.close}
        onUserAdded={handleUserAdded}
      />

      {bulkPermissionMode && (
        <BulkPermissionsModal
          opened
          mode={bulkPermissionMode}
          employees={selectedEmployeeTargets}
          close={() => setBulkPermissionMode(null)}
          onCompleted={() => {
            setBulkSelectedIds(new Set())
            handleRefetchEmployees()
          }}
          onPartialFailure={(failedEmployeeIds) => {
            setBulkSelectedIds(new Set(failedEmployeeIds))
            handleRefetchEmployees()
          }}
        />
      )}

      {detailsDrawerOpened && (
        <UserDetailsDrawer
          opened={detailsDrawerOpened}
          close={() => {
            detailsDrawerHandlers.close()
            setSelectedEmployeeId(null)
          }}
          employeeId={selectedEmployeeId}
          refetchEmployees={handleRefetchEmployees}
        />
      )}

      {removeModalOpened && (
        <RemoveUserModal
          opened={removeModalOpened}
          close={() => {
            removeModalHandlers.close()
            setEmployeePendingRemoval(null)
          }}
          employee={employeePendingRemoval}
          refetchEmployees={handleRefetchEmployees}
        />
      )}
    </div>
  )
}

export default UsersTab
